/**
 * Zod schemas for the `events` collection.
 *
 * These describe the shape of a complete Event document as stored in
 * Firestore. They are the single source of truth — the TypeScript `Event`
 * interface is derived from `eventDocumentSchema` via `z.infer<>` plus an
 * intersection with `BaseDocument` (id / createdAt / updatedAt are added
 * by Firestore and aren't part of the schema). See ADR-083 for the
 * architectural rationale and ADR-016 for the existing type contract pattern
 * this layer codifies.
 *
 * **Migration template (mirrored across the other 21 collections).**
 *
 * Shared schema layer:
 *   - `xxxDocumentSchema` — the full document shape; every field a Zod
 *     primitive or composed schema. No form-level validation messages
 *     beyond what the field permits structurally (a Role enum is fine, a
 *     `min(1, "Required")` belongs in the admin form layer).
 *   - Companion sub-schemas exported when they appear elsewhere
 *     (`eventConfigSchema`, `sessionTypeConfigSchema`).
 *
 * Admin form layer (`apps/admin/src/schemas/eventSchema.ts`):
 *   - Imports `eventDocumentSchema`, omits server-set / deferred fields,
 *     overrides constrained fields with `min(1, "...")` / regex / narrowed
 *     enums, and adds cross-field `.refine()` rules.
 *   - Uses Zod 4 shape-destructure pattern
 *     (`z.object({ ...documentSchema.shape, name: z.string().min(...) })`)
 *     for best TypeScript compiler perf — avoids the `.extend()` inheritance
 *     chain that historically caused tsc instantiation explosions.
 */

import { z } from 'zod';
import type { ColorTokens } from '../theme/colors';
import type { FontFamilies } from '../theme/typography';

/** CSS gradient direction for the hero overlay scrim. */
export const heroOverlayDirectionSchema = z.enum([
  'to top',
  'to bottom',
  'to left',
  'to right',
]);

export const eventStatusSchema = z.enum(['draft', 'published', 'archived']);

/** A single Question/Answer pair in an event's FAQ list. */
export const faqEntrySchema = z.object({
  id: z.string(),
  question: z.string(),
  answer: z.string(),
});

/** Organizer-defined session category with default audience targeting. */
export const sessionTypeConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Hex background color for agenda cards. */
  backgroundColor: z.string(),
  /** Hex text color for agenda cards. */
  textColor: z.string(),
  /**
   * Lucide icon name (e.g. `"Presentation"`, `"Coffee"`) rendered on the
   * mobile agenda row's leading circle. The curated set lives in
   * `packages/shared/src/constants/sessionTypeIcons.ts`; storage is
   * permissive (any string or null) so older docs without the field are
   * not runtime errors. Renderers fall back to `Calendar` when null or
   * unknown.
   */
  iconName: z.string().nullable(),
  /**
   * Default Audience ID that newly created sessions of this type
   * inherit into their `audienceId`. Existing sessions are independent —
   * changing this value does NOT retroactively rewrite sessions. Null
   * means "no default" (the session form requires the organizer to pick
   * an audience explicitly). The admin form references the per-event
   * named audience set (custom groups + role audiences). ADR-120
   * collapsed the prior `defaultGroupIds: string[]` to a single
   * reference.
   */
  defaultAudienceId: z.string().nullable(),
});


export const eventConfigSchema = z.object({
  /** Label overrides — null uses the default label. */
  agendaLabel: z.string().nullable(),
  myScheduleLabel: z.string().nullable(),
  speakersLabel: z.string().nullable(),
  sessionsLabel: z.string().nullable(),

  /** Organizer-defined session categories with default audience targeting. */
  sessionTypes: z.array(sessionTypeConfigSchema),

  /** Per-event FAQ entries shown to attendees in the mobile app. */
  faqs: z.array(faqEntrySchema),
});

/**
 * The complete Event document shape (excluding BaseDocument fields:
 * id / createdAt / updatedAt, which are added by Firestore).
 *
 * `customTheme` and `customFonts` reference theme-token interfaces from
 * `packages/shared/src/theme/`. Those types are internally controlled by
 * the admin theme-picker UI (not free user input), so we use
 * `z.custom<T>()` to anchor the TypeScript type without paying the cost
 * of writing per-token Zod schemas. Runtime validation can be added at
 * any integration boundary that needs it.
 */
export const eventDocumentSchema = z.object({
  /**
   * Firebase Auth UID of the organizer who created this event. Mirrors the
   * `createdByUserId` convention on Notification and `createdByUid` on
   * Invitation.
   */
  createdByUserId: z.string(),
  name: z.string(),
  description: z.string().nullable(),

  /**
   * Reference to the event's venue in the `eventLocations` collection.
   * That document is the single source of truth for the venue's name,
   * address parts, coordinates, and color. The Event document carries
   * no freeform location strings of its own — display surfaces (event
   * card, ICS, SharePoint mirror) join against the linked location.
   *
   * Null for draft events without a venue set yet.
   */
  venueLocationId: z.string().nullable(),

  startDate: z.string(),
  endDate: z.string(),
  /** IANA timezone, e.g. "America/New_York". */
  timezone: z.string(),

  status: eventStatusSchema,

  // Branding
  heroImageURL: z.string().nullable(),
  /**
   * BlurHash string for the hero image. Decoded client-side into a tiny
   * fuzzy preview that paints instantly while the real image loads,
   * eliminating the white flash on cards and hero headers. Generated at
   * upload time in the admin EventForm and hardcoded for the bundled
   * seed images (see docs/architecture/hero-blurhash.md). Null for
   * legacy events created before this field existed; consumers fall
   * back to a solid color placeholder driven by heroOverlayColor.
   */
  heroBlurhash: z.string().nullable(),
  heroTitleOverride: z.string().nullable(),
  heroOverlayColor: z.string().nullable(),
  heroOverlayOpacity: z.number().nullable(),
  heroOverlayDirection: heroOverlayDirectionSchema.nullable(),
  customTheme: z.custom<Partial<ColorTokens> | null>(),
  customFonts: z.custom<Partial<FontFamilies> | null>(),

  /** Per-event label overrides and session category definitions. */
  config: eventConfigSchema,
});
